import { useQuery } from '@apollo/client/react';
import { plural, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useField } from 'formik';
import { getUserRoleName } from 'app/domains/users';
import { AclAction } from 'shared/domains/apollo/generated/types';
import { AccessManagementListDocument } from './AccessManagementList.gql';
export function useAccessManagementList(options) {
    const { entryId, parentId } = options;
    const [teamField] = useField('teamAcls');
    const [userField] = useField('userAcls');
    const { i18n } = useLingui();
    const { data, error, loading, refetch } = useQuery(AccessManagementListDocument, {
        variables: {
            entityId: entryId || '',
            hasEntityId: Boolean(entryId),
            parentId: parentId || '',
            hasParentId: Boolean(parentId),
        },
    });
    const { parent, teams, companyUsers, entity } = data || {};
    /**
     * Parent is needed in order to understand if the user has access to a parent folder.
     * If no entityId is provided, we will look in to the provided parentId to determinate that.
     */
    const parentEntity = (entity === null || entity === void 0 ? void 0 : entity.parent) || parent;
    const accessToParentEntry = (acl) => Boolean(acl === null || acl === void 0 ? void 0 : acl.actions.some((action) => action === AclAction.Read || action === AclAction.Write));
    const userAcls = userField.value.map((userAcl, index) => {
        var _a;
        const companyUser = companyUsers === null || companyUsers === void 0 ? void 0 : companyUsers.find((userData) => userData.userId === userAcl.userId);
        const title = ((_a = companyUser === null || companyUser === void 0 ? void 0 : companyUser.user) === null || _a === void 0 ? void 0 : _a.name) || t({ message: 'Deleted user', comment: 'Unknown user name' });
        const subtitle = i18n._(getUserRoleName(companyUser));
        const parentAcl = parentEntity === null || parentEntity === void 0 ? void 0 : parentEntity.acls.find((acl) => acl.userId === userAcl.userId);
        const hasAccessToParentFolder = accessToParentEntry(parentAcl);
        const isTraverse = userAcl.actions.includes(AclAction.Traverse);
        return {
            id: userAcl.userId,
            fieldName: `${userField.name}[${index}]`,
            title,
            subtitle,
            hasAccessToParentFolder,
            visible: !isTraverse,
        };
    });
    const teamAcls = teamField.value.map((teamAcl, index) => {
        var _a;
        const team = teams === null || teams === void 0 ? void 0 : teams.find((teamData) => teamData.id === teamAcl.teamId);
        /**
         * NOTE
         * Project feature assign team ACL's to documents when added to a project. And we want to
         * filter out those. If no team is found, we will assume it is a "Project team ACL".
         */
        const hasTeam = Boolean(team === null || team === void 0 ? void 0 : team.id);
        const isTraverse = teamAcl.actions.includes(AclAction.Traverse);
        const visible = hasTeam && !isTraverse;
        const parentAcl = parentEntity === null || parentEntity === void 0 ? void 0 : parentEntity.teamAcls.find((acl) => acl.teamId === teamAcl.teamId);
        const hasAccessToParentFolder = accessToParentEntry(parentAcl);
        const memberCount = ((_a = team === null || team === void 0 ? void 0 : team.members) === null || _a === void 0 ? void 0 : _a.length) || 0;
        const unknownTeam = t({ message: 'Unknown team', comment: 'Unknown team' });
        return {
            id: teamAcl.teamId,
            fieldName: `${teamField.name}[${index}]`,
            title: (team === null || team === void 0 ? void 0 : team.name) || unknownTeam,
            subtitle: t({ message: plural(memberCount, { one: '# member', other: '# members' }) }),
            hasAccessToParentFolder,
            visible,
        };
    });
    return {
        loading,
        error,
        refetch,
        userAcls: {
            value: userAcls,
            name: userField.name,
        },
        teamAcls: {
            value: teamAcls,
            name: teamField.name,
        },
    };
}
